/**
 * @voltxampere/presets — Preset barrel
 *
 * Sprint 17 set, in display order. The registry is built from this
 * array — keep the order intentional (the dialog renders presets
 * left-to-right, top-to-bottom in this sequence).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { voltageDivider } from "./voltage-divider.js";
import { rcLowPass } from "./rc-low-pass.js";
import { rcHighPass } from "./rc-high-pass.js";
import { ledCurrentLimiter } from "./led-current-limiter.js";
import { rlCircuit } from "./rl-circuit.js";
import { rlcSeriesResonance } from "./rlc-series-resonance.js";
import { diodeClipper } from "./diode-clipper.js";
import { bridgeRectifier } from "./bridge-rectifier.js";
import { npnCommonEmitter } from "./npn-common-emitter.js";
import { opampNonInverting } from "./opamp-non-inverting.js";
import { opampInverting } from "./opamp-inverting.js";
import { opampBuffer } from "./opamp-buffer.js";
import { transistorSwitch } from "./transistor-switch.js";
import { nmosSwitch } from "./nmos-switch.js";
import { zenerRegulator } from "./zener-regulator.js";
// Sprint 23 H1 + ADR-0048 — Op-Amp astable (555 alternative).
import { opampAstable } from "./opamp-astable.js";
// Sprint 50-2 ADR-0072 — beginner expansion (3 yeni preset).
import { simpleResistorPair } from "./simple-resistor-pair.js";
import { parallelResistors } from "./parallel-resistors.js";
import { threeResistorDivider } from "./three-resistor-divider.js";
// Sprint 50-3 ADR-0072 — power expansion (3 yeni preset).
import { halfWaveRectifier } from "./half-wave-rectifier.js";
import { voltageDoubler } from "./voltage-doubler.js";
import { rcPowerDecoupling } from "./rc-power-decoupling.js";
// Sprint 50-4 ADR-0072 — filter + oscillator expansion (3 yeni preset, oscillator NEW kategori).
import { rcBandpass } from "./rc-bandpass.js";
import { wienBridgeOsc } from "./wien-bridge-osc.js";
import { rcPhaseShiftOsc } from "./rc-phase-shift-osc.js";
// Sprint 50-5 ADR-0072 — logic expansion (3 yeni preset).
import { digitalAndGate } from "./digital-and-gate.js";
import { digitalXorGate } from "./digital-xor-gate.js";
import { digitalNotGate } from "./digital-not-gate.js";
// Sprint 50-6 ADR-0072 — MCU expansion (2 yeni preset, mcu NEW kategori).
import { arduinoBlinkLed } from "./arduino-blink-led.js";
import { arduinoButtonInput } from "./arduino-button-input.js";
// Sprint 54.3-1 ADR-0087 — Father's circuit canonical regression
// devresi (VISION §10). 50+ component proxy, engine extension #5
// regression case. Sprint 54.3d-2 adds the transient variant with
// V_mains as ac-source(311, 50) for vxa-engine ↔ ngspice waveform
// parity (engine extension #6, ADR-0095).
import { fathersCapBankController, fathersCapBankControllerTransient, } from "./fathers-cap-bank-controller.js";
export const ALL_PRESETS = Object.freeze([
    voltageDivider,
    rcLowPass,
    rcHighPass,
    ledCurrentLimiter,
    rlCircuit,
    rlcSeriesResonance,
    diodeClipper,
    bridgeRectifier,
    npnCommonEmitter,
    opampNonInverting,
    opampInverting,
    opampBuffer,
    transistorSwitch,
    nmosSwitch,
    zenerRegulator,
    // Sprint 23 H1 — 16th preset, NE555 educational alternative.
    opampAstable,
    // Sprint 50-2 — beginner expansion (basic kategori).
    simpleResistorPair,
    parallelResistors,
    threeResistorDivider,
    // Sprint 50-3 — power expansion (power kategori).
    halfWaveRectifier,
    voltageDoubler,
    rcPowerDecoupling,
    // Sprint 50-4 — filter + oscillator expansion (oscillator NEW kategori).
    rcBandpass,
    wienBridgeOsc,
    rcPhaseShiftOsc,
    // Sprint 50-5 — logic expansion (digital kategori).
    digitalAndGate,
    digitalXorGate,
    digitalNotGate,
    // Sprint 50-6 — MCU expansion (mcu NEW kategori).
    arduinoBlinkLed,
    arduinoButtonInput,
    // Sprint 54.3-1 — Father's circuit (VISION §10 regression).
    fathersCapBankController,
    // NOTE: fathersCapBankControllerTransient is NOT in ALL_PRESETS.
    // It is a parity-corpus-only variant exported via the package
    // boundary for vxa-engine ↔ ngspice transient regression tests
    // (Sprint 54.3d-2). DEVRELER UI only surfaces the DC variant.
]);
export { voltageDivider, rcLowPass, rcHighPass, ledCurrentLimiter, rlCircuit, rlcSeriesResonance, diodeClipper, bridgeRectifier, npnCommonEmitter, opampNonInverting, opampInverting, opampBuffer, transistorSwitch, nmosSwitch, zenerRegulator, opampAstable, simpleResistorPair, parallelResistors, threeResistorDivider, halfWaveRectifier, voltageDoubler, rcPowerDecoupling, rcBandpass, wienBridgeOsc, rcPhaseShiftOsc, digitalAndGate, digitalXorGate, digitalNotGate, arduinoBlinkLed, arduinoButtonInput, fathersCapBankController, fathersCapBankControllerTransient, };
//# sourceMappingURL=index.js.map